// Angular import
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';

// project import
import { VMSConfig } from 'src/app/app-config';
import { SharedModule } from 'src/app/theme/shared/shared.module';

// third party
import { TranslateService } from '@ngx-translate/core';
import screenfull from 'screenfull';
import { ConfigurationComponent } from '../../configuration/configuration.component';
import { AuthService } from 'src/app/dashboard/users/login/auth.service';
import { UsersService } from 'src/app/dashboard/users/users.service';
import { pagesAccessFunctions } from 'src/app/theme/shared/access-functions';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import { NgSelectModule } from "@ng-select/ng-select";
import { A11yModule } from "@angular/cdk/a11y";

@Component({
    selector: 'app-nav-right',
    imports: [SharedModule, RouterModule, ConfigurationComponent, NgSelectModule, A11yModule],
    templateUrl: './nav-right.component.html',
    styleUrl: './nav-right.component.scss'
})
export class NavRightComponent implements OnInit {
	user = null;
	screenFull: boolean = true;
	componentSections = [
		{
			name: 'users_management',
			title: this.translate.instant('nav.users_management'),
			items: [
				{
					title: this.translate.instant('nav.users_list'),
					url: '/users',
					accessFunctions: pagesAccessFunctions.user_list
				},
				{
					title: this.translate.instant('nav.add_user'),
					url: '/users/create',
					accessFunctions: pagesAccessFunctions.user_create
				},
				{
					title: this.translate.instant('nav.user_role'),
					url: '/user-roles',
					accessFunctions: pagesAccessFunctions.user_roles
				}
			]
		},
		{
			name: 'devices_management',
			title: this.translate.instant('nav.devices_management'),
			items: [
				{
					title: this.translate.instant('nav.devices_list'),
					url: '/devices',
					accessFunctions: pagesAccessFunctions.device_list
				},
				{
					title: this.translate.instant('nav.reject_device_connections_logs'),
					url: '/rejected-devices-connection',
					accessFunctions: pagesAccessFunctions.rejection_devices_logs
				},
				{
					title: this.translate.instant('nav.device_restart_count_report'),
					url: '/device-restart-count-report',
					accessFunctions: pagesAccessFunctions.restart_count_report
				},
				{
					title: this.translate.instant('nav.bwt_connectivity_report'),
					url: '/bwt-connectivity-report',
					accessFunctions: pagesAccessFunctions.bwt_connectivity_report
				},
				{
					title: this.translate.instant('nav.device_replacement_log'),
					url: '/device-replacement-log',
					accessFunctions: pagesAccessFunctions.device_replacement_log
				},
				{
					title: this.translate.instant('nav.firmware_update'),
					url: '/firmware-update',
					accessFunctions: pagesAccessFunctions.firmware_update
				}
			]
		},
		{
			name: 'admin_tools',
			title: this.translate.instant('nav.admin_tools'),
			items: [
				{
					title: this.translate.instant('nav.api_logs'),
					url: '/api-logs',
					accessFunctions: pagesAccessFunctions.api_logs
				},
				{
					title: this.translate.instant('nav.fw_upload'),
					url: '/fw-upload',
					accessFunctions: pagesAccessFunctions.fw_upload
				},
				{
					title: this.translate.instant('nav.fw_logs'),
					url: '/fw-logs',
					accessFunctions: pagesAccessFunctions.fw_logs
				},
				{
					title: this.translate.instant('nav.action_log'),
					url: '/action-log',
					accessFunctions: pagesAccessFunctions.action_log
				},
				{
					title: this.translate.instant('nav.cm_fw_versions'),
					url: '/cm-fw-versions',
					accessFunctions: pagesAccessFunctions.cm_fw_versions
				},
				{
					title: this.translate.instant('nav.schedule_run'),
					url: '/schedule-run',
					accessFunctions: pagesAccessFunctions.schedule_run
				},
				{
					title: this.translate.instant('nav.sim_management'),
					url: '/sim-management',
					accessFunctions: pagesAccessFunctions.sim_management,
				},
			]
		},
		{
			name: 'shipment',
			title: this.translate.instant('nav.shipment'),
			items: [
				{
					title: this.translate.instant('nav.receive_shipment'),
					url: '/shipment/receive-shipment',
					accessFunctions: pagesAccessFunctions.receive_shipment
				},
				{
					title: this.translate.instant('nav.shipment_quality'),
					url: '/shipment/shipment-quality',
					accessFunctions: pagesAccessFunctions.shipment_quality
				},
				{
					title: this.translate.instant('nav.shipping_out'),
					url: '/shipment/shipping-out',
					accessFunctions: pagesAccessFunctions.shipping_out
				},
				{
					title: this.translate.instant('nav.rma_devices'),
					url: '/shipment/rma-devices',
					accessFunctions: pagesAccessFunctions.rma_devices,
				},
				{
					title: this.translate.instant('nav.orders_management'),
					url: '/shipment/orders-management',
					accessFunctions: pagesAccessFunctions.orders_management
				},
			]
		}
	];

	timezones: Array<{value: number, label: string}> = [];
	selectedTimezone: { value: number; label: string } | null = null;

	constructor(
		public authService: AuthService,
		private translate: TranslateService,
		public usersService: UsersService,
		private commonDataService: CommonDataService
	) {}

	ngOnInit() {
		this.user = this.authService.getCurrentUser();
		this.selectedTimezone = this.commonDataService.getCurrentUserTimeZone();
		this.timezones = this.commonDataService.timeZoneMenu.map(tz => ({ value: tz.id, label: tz.displayName }));

		setTimeout(() => {
			this.useLanguage(VMSConfig.i18n);
		}, 0);
	}

	canAccessTheNavMenu() {
		for (const section of this.componentSections) {
			for (const item of section.items) {
				if (this.usersService.hasAccessFunctions(item.accessFunctions)) {
					return true;
				}
			}
		}

		return false;
	}

	canAccessSection(name: string) {
		const section = this.componentSections.find(sec => sec.name === name);

		for (const item of section.items) {
			if (this.usersService.hasAccessFunctions(item.accessFunctions)) {
				return true;
			}
		}

		return false;
	}

	logout() {
		this.authService.logoutUser();
	}

	// user according language change of sidebar menu item
	useLanguage(language: string) {
		this.translate.use(language);
	}

	// full screen toggle
	toggleFullscreen() {
		this.screenFull = screenfull.isFullscreen;
		if (screenfull.isEnabled) {
			screenfull.toggle();
		}
	}

	updateTimezone() {
		this.commonDataService.setTimezone(this.selectedTimezone);
	}

}
